import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

export const NAV_LINKS = [
  { label: "Home", id: "home", testId: "nav-link-home" },
  { label: "About", id: "about", testId: "nav-link-about" },
  { label: "Clinical Expertise", id: "expertise", testId: "nav-link-expertise" },
  { label: "Education", id: "education", testId: "nav-link-education" },
  { label: "Academic Profile", id: "academic", testId: "nav-link-academic" },
  { label: "Little Ones", id: "little-ones", testId: "nav-link-little-ones" },
  { label: "Contact", id: "contact", testId: "nav-link-contact" },
];

export const BrandMark = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect width="64" height="64" rx="18" fill="#70435F" />
    <text x="31" y="42" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="32" fontWeight="600" fill="#F6F1EC" textAnchor="middle">O</text>
    <circle cx="46" cy="19" r="3.2" fill="#B89A69" />
  </svg>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-38% 0px -55% 0px" }
    );
    NAV_LINKS.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#F6F1EC]/92 backdrop-blur-md border-b border-[#70435F]/10 py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
          <button
            data-testid="nav-brand-title"
            onClick={() => go("home")}
            className="flex items-center gap-3 group"
            aria-label="Back to top — Dr. Ojaswini Sheshrao Gajbhiye"
          >
            <BrandMark />
            <span className="text-left leading-tight">
              <span className="font-display block text-lg font-semibold text-[#383237] group-hover:text-[#70435F] transition-colors">
                Dr. Ojaswini Gajbhiye
              </span>
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#8A7E88]">
                Pediatrician · MBBS DCH DNB
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId}
                onClick={() => go(l.id)}
                className={`text-[12.5px] font-medium uppercase tracking-[0.12em] transition-colors relative pb-1 ${
                  active === l.id ? "text-[#70435F]" : "text-[#695E67] hover:text-[#70435F]"
                }`}
              >
                {l.label}
                <span
                  className={`absolute left-0 -bottom-0.5 h-[2px] bg-[#B89A69] transition-all duration-500 ${
                    active === l.id ? "w-full" : "w-0"
                  }`}
                  aria-hidden="true"
                />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-contact"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex bg-[#70435F] text-white hover:bg-[#5A344C] px-5 py-2.5 rounded-full text-sm font-medium transition-colors shadow-sm"
            >
              Get in Touch
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 text-[#70435F]"
              aria-label="Open menu"
            >
              <Menu size={26} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[70] bg-[#F6F1EC] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <BrandMark />
              <button
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                className="p-2 text-[#70435F]"
                aria-label="Close menu"
              >
                <X size={28} strokeWidth={1.6} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="font-display text-left text-4xl font-medium text-[#383237] hover:text-[#70435F] transition-colors py-2 border-b border-[#E7DED4]"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.p
                className="mt-8 text-xs uppercase tracking-[0.22em] text-[#8A7E88]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                Every child deserves a healthier beginning.
              </motion.p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
